import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/hooks/use-toast';

export interface Competitor {
  id: string;
  user_id: string;
  instagram_username: string;
  display_name?: string;
  profile_image_url?: string;
  is_active: boolean;
  added_at: string;
}

export interface CompetitorDisplay extends Competitor {
  followers?: number;
  following?: number;
  posts?: number;
  engagementRate?: number;
}

export const useCompetitors = (maxCompetitors: number = 5) => {
  const [competitors, setCompetitors] = useState<CompetitorDisplay[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchCompetitors = async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('competitors')
        .select('*')
        .eq('user_id', user.id)
        .order('added_at', { ascending: false });

      if (error) {
        console.error('Error fetching competitors:', error);
        toast({
          title: "Błąd",
          description: "Nie udało się załadować konkurentów",
          variant: "destructive",
        });
        return;
      }

      // Batch fetch latest metrics for all competitors in a single query
      const competitorIds = data.map(c => c.id);
      const { data: allMetrics } = competitorIds.length > 0
        ? await supabase
            .from('competitor_daily_metrics')
            .select('*')
            .eq('user_id', user.id)
            .in('competitor_id', competitorIds)
            .order('metric_date', { ascending: false })
        : { data: [] };

      // Group by competitor_id and take the most recent entry for each
      const latestMetricsByCompetitor = new Map<string, typeof allMetrics extends (infer T)[] | null ? T : never>();
      allMetrics?.forEach(metric => {
        if (!latestMetricsByCompetitor.has(metric.competitor_id)) {
          latestMetricsByCompetitor.set(metric.competitor_id, metric);
        }
      });

      const competitorsWithMetrics = data.map(competitor => {
        const metricsData = latestMetricsByCompetitor.get(competitor.id);

        let engagementRate = 0;
        if (metricsData && metricsData.followers_count && metricsData.followers_count > 0) {
          const totalEngagement = (metricsData.latest_post_likes || 0) + (metricsData.latest_post_comments || 0);
          engagementRate = Math.round((totalEngagement / metricsData.followers_count) * 100 * 10) / 10;
        }

        return {
          ...competitor,
          followers: metricsData?.followers_count || 0,
          following: metricsData?.following_count || 0,
          posts: metricsData?.posts_count || 0,
          engagementRate,
        };
      });

      setCompetitors(competitorsWithMetrics);
    } catch (error) {
      console.error('Error in fetchCompetitors:', error);
      toast({
        title: "Błąd",
        description: "Nie udało się załadować konkurentów",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addCompetitor = async (usernameOrUrl: string): Promise<boolean> => {
    if (!user) {
      toast({
        title: "Błąd",
        description: "Musisz być zalogowany, aby dodać konkurentów",
        variant: "destructive",
      });
      return false;
    }

    if (!usernameOrUrl.trim()) {
      toast({
        title: "Błąd",
        description: "Wprowadź poprawną nazwę użytkownika Instagram lub URL",
        variant: "destructive",
      });
      return false;
    }

    // Check slot limit before insert
    if (competitors.length >= maxCompetitors) {
      toast({
        title: "Limit osiągnięty",
        description: `Osiągnięto limit ${maxCompetitors} konkurentów. Usuń jednego, aby dodać nowego.`,
        variant: "destructive",
      });
      return false;
    }

    // Extract username from URL or use directly
    const username = usernameOrUrl
      .replace(/.*instagram\.com\//, '')
      .replace(/\/$/, '')
      .split('/')[0];
    
    // Check if already exists
    if (competitors.some(comp => comp.instagram_username === username)) {
      toast({
        title: "Błąd",
        description: "Ten konkurent jest już na Twojej liście",
        variant: "destructive",
      });
      return false;
    }

    try {
      const { data, error } = await supabase
        .from('competitors')
        .insert({
          user_id: user.id,
          instagram_username: username,
          display_name: username,
          is_active: true
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding competitor:', error);
        // Parse tier-based limit errors from DB trigger
        if (error.message?.includes('konkurentów osiągnięty') || error.message?.includes('competitors reached')) {
          toast({
            title: "Limit osiągnięty",
            description: "Osiągnięto limit konkurentów dla Twojego planu. Usuń jednego, aby dodać nowego.",
            variant: "destructive",
          });
        } else if (error.message?.includes('Miesięczny limit') || error.message?.includes('Monthly limit')) {
          toast({
            title: "Limit miesięczny",
            description: "Wykorzystano limit dodań konkurentów w tym miesiącu. Spróbuj ponownie w następnym okresie.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Błąd",
            description: "Nie udało się dodać konkurenta do bazy danych",
            variant: "destructive",
          });
        }
        return false;
      }

      // Add the new competitor to local state with default metrics (will be updated when data is scraped)
      const newCompetitorWithMetrics = {
        ...data,
        followers: 0,
        following: 0,
        posts: 0,
        engagementRate: 0,
      };

      setCompetitors(prev => [newCompetitorWithMetrics, ...prev]);
      
      return true;
    } catch (error) {
      console.error('Error in addCompetitor:', error);
      toast({
        title: "Błąd",
        description: "Nie udało się dodać konkurenta",
        variant: "destructive",
      });
      return false;
    }
  };

  const toggleObservation = async (competitorId: string): Promise<boolean> => {
    const competitor = competitors.find(c => c.id === competitorId);
    if (!competitor) return false;

    try {
      const newIsActive = !competitor.is_active;
      
      const { error } = await supabase
        .from('competitors')
        .update({ is_active: newIsActive })
        .eq('id', competitorId);

      if (error) {
        console.error('Error updating competitor:', error);
        toast({
          title: "Błąd",
          description: "Nie udało się zaktualizować statusu obserwacji",
          variant: "destructive",
        });
        return false;
      }

      // Update local state
      setCompetitors(prev => prev.map(comp =>
        comp.id === competitorId
          ? { ...comp, is_active: newIsActive }
          : comp
      ));

      return true;
    } catch (error) {
      console.error('Error in toggleObservation:', error);
      toast({
        title: "Błąd",
        description: "Nie udało się zaktualizować statusu obserwacji",
        variant: "destructive",
      });
      return false;
    }
  };

  const removeCompetitor = async (competitorId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('competitors')
        .delete()
        .eq('id', competitorId);

      if (error) {
        console.error('Error removing competitor:', error);
        toast({
          title: "Błąd",
          description: "Nie udało się usunąć konkurenta",
          variant: "destructive",
        });
        return false;
      }

      setCompetitors(prev => prev.filter(comp => comp.id !== competitorId));
      
      return true;
    } catch (error) {
      console.error('Error in removeCompetitor:', error);
      toast({
        title: "Błąd",
        description: "Nie udało się usunąć konkurenta",
        variant: "destructive",
      });
      return false;
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchCompetitors();
    }
  }, [user?.id]);

  return {
    competitors,
    isLoading,
    addCompetitor,
    toggleObservation,
    removeCompetitor,
    refetch: fetchCompetitors
  };
};